import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import report from './reports/miami-used-car-market-2026-08.json';

export const MIAMI_RESEARCH_PATH = '/research/miami-used-car-market-august-2026';
export const MIAMI_RESEARCH_URL = `https://meshum.com${MIAMI_RESEARCH_PATH}`;
export const MIAMI_RESEARCH_TITLE = 'Miami Used Car Market Report — August 2026 | Meshum';
export const MIAMI_RESEARCH_DESCRIPTION = 'Explore August 2026 Miami used-car inventory, Meshum Estimate coverage, below-estimate pricing, median savings, and represented dealers.';

const integer = new Intl.NumberFormat('en-US');
const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
const percent = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const asOfDate = new Date(report.asOfUtc);
const displayDate = new Intl.DateTimeFormat('en-US', {
  month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
}).format(asOfDate);

const metrics = [
  ['Active listings analyzed', integer.format(report.activeListingsAnalyzed)],
  ['With usable Meshum Estimate', integer.format(report.listingsWithUsableMestimate)],
  ['Meshum Estimate coverage', `${percent.format(report.mestimateCoveragePct)}%`],
  ['Below estimate', integer.format(report.belowEstimateListings)],
  ['Median amount below', currency.format(report.medianBelowEstimateAmount)],
  ['Median percentage below', `${percent.format(report.medianBelowEstimatePct)}%`],
  ['Dealers represented', integer.format(report.dealersRepresented)],
];

export default function MiamiUsedCarMarketAugust2026() {
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://meshum.com/' },
      { '@type': 'ListItem', position: 2, name: 'Research', item: 'https://meshum.com/research' },
      { '@type': 'ListItem', position: 3, name: 'Miami Used Car Market Report — August 2026', item: MIAMI_RESEARCH_URL },
    ],
  };
  const article = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Miami Used Car Market Report — August 2026',
    description: MIAMI_RESEARCH_DESCRIPTION,
    url: MIAMI_RESEARCH_URL,
    datePublished: report.asOfUtc,
    dateModified: report.asOfUtc,
    genre: 'Used car market research report',
    spatialCoverage: { '@type': 'Place', name: 'Miami, Florida metropolitan market' },
    author: { '@type': 'Organization', name: 'Meshum' },
    publisher: {
      '@type': 'Organization',
      name: 'Meshum',
      url: 'https://meshum.com',
      logo: { '@type': 'ImageObject', url: 'https://meshumimages.blob.core.windows.net/websiteimages/1.png' },
    },
    mainEntityOfPage: MIAMI_RESEARCH_URL,
  };

  return (
    <main className="bg-slate-50 text-slate-900">
      <Helmet defer={false}>
        <title>{MIAMI_RESEARCH_TITLE}</title>
        <meta name="description" content={MIAMI_RESEARCH_DESCRIPTION} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={MIAMI_RESEARCH_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={MIAMI_RESEARCH_TITLE} />
        <meta property="og:description" content={MIAMI_RESEARCH_DESCRIPTION} />
        <meta property="og:url" content={MIAMI_RESEARCH_URL} />
        <meta property="og:image" content="https://meshum.com/share-thumbnail.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={MIAMI_RESEARCH_TITLE} />
        <meta name="twitter:description" content={MIAMI_RESEARCH_DESCRIPTION} />
        <meta name="twitter:image" content="https://meshum.com/share-thumbnail.jpg" />
        <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
        <script type="application/ld+json">{JSON.stringify(article)}</script>
      </Helmet>

      <article className="mx-auto max-w-6xl px-5 py-10 sm:px-6 lg:px-8 lg:py-14">
        <nav aria-label="Breadcrumb" className="text-sm text-slate-600">
          <Link to="/" className="hover:text-blue-700">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/research" className="hover:text-blue-700">Research</Link>
          <span className="mx-2">/</span>
          <span aria-current="page">Miami market report</span>
        </nav>

        <header
          data-testid="research-report-hero"
          className="mt-8 grid min-w-0 grid-cols-1 gap-5 border-0 bg-transparent px-0 py-0 lg:grid-cols-[minmax(0,2fr)_minmax(220px,1fr)] lg:items-end lg:gap-10"
        >
          <div className="min-w-0">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-blue-700">Meshum Research</p>
            <h1 className="mt-3 max-w-none text-[clamp(2rem,4vw,3.25rem)] font-semibold leading-[1.07] tracking-tight">
              Miami Used Car Market Report — August 2026
            </h1>
            <p className="mt-5 max-w-3xl text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
              Meshum analyzed active dealer used-car listings within 50 miles of ZIP 33131 and their persisted Meshum Estimates to measure local valuation coverage and below-estimate pricing.
            </p>
          </div>
          <p className="m-0 min-w-0 text-sm font-medium leading-6 text-slate-500 lg:border-l lg:border-slate-200 lg:pl-6">
            Frozen report as of {displayDate} at {asOfDate.toISOString().slice(11, 16)} UTC
          </p>
        </header>

        <section aria-labelledby="market-snapshot" className="mt-10">
          <h2 id="market-snapshot" className="text-3xl font-semibold tracking-tight">Market snapshot</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {metrics.map(([label, value]) => (
              <div key={label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-sm font-medium text-slate-600">{label}</div>
                <div className="mt-2 text-3xl font-semibold text-slate-950">{value}</div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-sm leading-6 text-slate-600">
            Of listings with a usable Meshum Estimate, {percent.format(report.belowEstimatePctOfUsable)}% met Meshum's established Below Estimate positioning rules.
          </p>
        </section>

        <section className="mt-12 grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-semibold">What the data shows</h2>
            <p className="mt-4 leading-7 text-slate-600">
              Meshum represented {integer.format(report.activeListingsAnalyzed)} active listings from {integer.format(report.dealersRepresented)} dealers in the defined market. A usable persisted estimate was available for {percent.format(report.mestimateCoveragePct)}% of those listings.
            </p>
            <p className="mt-4 leading-7 text-slate-600">
              The {integer.format(report.belowEstimateListings)} listings classified Below Estimate had a median difference of {currency.format(report.medianBelowEstimateAmount)}, or {percent.format(report.medianBelowEstimatePct)}% below their Meshum Estimate. These figures describe asking-price position, not guaranteed transaction savings.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-semibold">How Meshum measures value</h2>
            <p className="mt-4 leading-7 text-slate-600">
              A Meshum Estimate uses supported, recently sold comparable vehicles and vehicle-level factors from the existing production methodology. For this report, an estimate had to be positive, version {report.methodology.currentMestimateVersion}, no more than {report.methodology.maxEstimateAgeDays} days old, and have an OK or LOW_CONFIDENCE status.
            </p>
            <p className="mt-4 leading-7 text-slate-600">
              Below Estimate uses the existing positioning rule: at least {currency.format(report.methodology.belowMinimumSavingsAmount)} and {percent.format(report.methodology.belowMinimumSavingsPct)}% below estimate, with the existing {percent.format(report.methodology.belowMaximumSavingsPct)}% sanity boundary. Exact medians use the middle value, or the average of the two middle values for an even count.
            </p>
          </div>
        </section>

        <section className="mt-12 rounded-xl bg-slate-950 p-7 text-white sm:p-9">
          <h2 className="text-3xl font-semibold">Find cars priced below their estimated value</h2>
          <p className="mt-3 max-w-3xl leading-7 text-slate-200">
            Explore current qualifying vehicles in the same fixed Miami market, or broaden your search to Meshum Best Deals nationwide.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Link to="/used-cars/miami-fl/best-deals" className="rounded-lg bg-white px-5 py-3 text-center font-semibold text-slate-950 no-underline">View Miami Best Deals</Link>
            <Link to="/insights/best-deals" className="rounded-lg border border-slate-600 px-5 py-3 text-center font-semibold text-white no-underline">Explore nationwide Best Deals</Link>
          </div>
        </section>

        <section className="mt-12 border-t border-slate-200 pt-8 text-sm leading-6 text-slate-600">
          <h2 className="text-xl font-semibold text-slate-950">Methodology and interpretation</h2>
          <p className="mt-3">
            The geographic scope is dealer inventory within {report.geography.radiusMiles} miles of ZIP {report.geography.centerZip}, using dealer ZIP coordinates and exact great-circle distance after a bounding-box filter. “Active listings analyzed” is not a claim about every vehicle available in Miami; it is active, unsold dealer inventory represented in Meshum at the report timestamp.
          </p>
          <p className="mt-3">
            “With usable Meshum Estimate” is a subset of analyzed listings. “Below estimate” is a subset of usable estimates and does not include every listing whose asking price is merely one dollar below its estimate. Meshum Estimates are informational, not appraisals, offers, or guarantees.
          </p>
          <p className="mt-3 font-mono text-xs text-slate-500">Report {report.reportVersion} · Rules {report.methodology.ruleVersion}</p>
          <Link to="/research" className="mt-5 inline-flex font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4">Browse all Meshum Research</Link>
        </section>
      </article>
    </main>
  );
}
